// /////////////////////////////////////////////////////////////
// [ファイル] ContactFormDialog.tsx
// [概要] SFA案件の取引先(法人)に紐づく「先方担当者」を登録・編集するダイアログ。
//        先方担当者の実体は CRM 個人顧客 (employer_id = 取引先の法人ID)。
//        新規登録時は、同一勤務先配下で メール / 氏名 / 携帯 が一致する既存顧客を
//        check_duplicate_contacts で検索し、重複を慎重に防ぐフローを挟む。
//        編集時は既存全項目を保持するため詳細を取得してマージのうえ送信する。
// /////////////////////////////////////////////////////////////
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
} from '@mui/material';
import axios from 'axios';
import * as Icons from '../constants/icons';
import LoadingView from './LoadingView';
import { useAuth } from '../firebase/AuthProvider';
import {
  STANDARD_CTA_BUTTON_SX,
  STANDARD_TEXT_BUTTON_SX,
} from '../constants/buttonStyles';
import { ep } from '../api/endpoints';

const UPSERT_URL = ep('upsert_crm_customer');
const DETAIL_URL = ep('get_crm_customer_detail');
const DUP_CHECK_URL = ep('check_duplicate_contacts');

export interface ContactFormDialogProps {
  open: boolean;
  onClose: () => void;
  employerId: string;        // 勤務先法人ID (= 案件の取引先 client_id)
  employerName?: string;
  contact?: any | null;      // 編集対象の行 (null/未指定なら新規登録)
  onSaved: () => void;       // 保存成功時に親へ通知 (一覧再取得)
}

// フォームで編集する先方担当者の項目。
// 連絡先は本人の私的連絡先(email/phone/mobile_phone)ではなく、
// 勤務先(業務上)の連絡先 business_* を扱う。
const EMPTY_FORM = {
  last_name: '',
  first_name: '',
  last_name_kana: '',
  first_name_kana: '',
  department: '',
  occupation: '',
  business_email: '',
  business_tel: '',
  business_mobile: '',
};

const ContactFormDialog: React.FC<ContactFormDialogProps> = ({
  open,
  onClose,
  employerId,
  employerName,
  contact,
  onSaved,
}) => {
  const { user } = useAuth();
  const isEdit = !!contact?.id;

  // formData は編集時は詳細全項目を保持する (update が全カラム上書きのため欠落させない)
  const [formData, setFormData] = useState<any>({ ...EMPTY_FORM });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 'input' = 入力, 'duplicate' = 重複候補の確認
  const [step, setStep] = useState<'input' | 'duplicate'>('input');
  const [candidates, setCandidates] = useState<any[]>([]);
  const [checking, setChecking] = useState(false);

  // ダイアログを開くたびに初期化。編集時は詳細を取得して全項目を保持。
  useEffect(() => {
    if (!open) return;
    setError(null);
    setStep('input');
    setCandidates([]);
    if (contact?.id) {
      setLoading(true);
      axios
        .post(DETAIL_URL, { customerId: contact.id })
        .then((res) => {
          if (res.data.status === 'success') {
            setFormData(res.data.detail);
          } else {
            // 詳細取得に失敗しても一覧の行データで最低限編集できるようにする
            setFormData({ ...EMPTY_FORM, ...contact });
          }
        })
        .catch(() => setFormData({ ...EMPTY_FORM, ...contact }))
        .finally(() => setLoading(false));
    } else {
      setFormData({ ...EMPTY_FORM });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contact?.id]);

  const handleChange = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
  };

  // 送信用ペイロードを組み立てる (employer_id と customer_type を必ず付与)
  // 詳細取得に含まれる複数連絡先 (contacts) は本ダイアログでは編集しないため送らない。
  // 送らないことでサーバーは business_* 列 → 子テーブル代表行の逆同期経路を通り整合が保たれる。
  const buildPayload = () => {
    const { contacts: _contacts, ...rest } = formData;
    return {
      customerId: isEdit ? contact.id : null,
      data: {
        ...rest,
        customer_type: 'individual',
        employer_id: employerId,
        operator_id: user?.email,
      },
    };
  };

  const doSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await axios.post(UPSERT_URL, buildPayload());
      if (res.data.status === 'success') {
        onSaved();
        onClose();
      } else {
        setError(res.data.message || '保存に失敗しました');
      }
    } catch (err: any) {
      console.error('Error saving contact:', err);
      setError(err.message || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  // 「次へ」: 新規時は重複チェックを実施。候補があれば確認ステップへ、なければそのまま登録。
  const handleNext = async () => {
    if (!formData.last_name) {
      setError('姓は必須です');
      return;
    }
    setError(null);

    // 編集時は重複チェック不要でそのまま保存
    if (isEdit) {
      await doSave();
      return;
    }

    setChecking(true);
    try {
      const res = await axios.post(DUP_CHECK_URL, {
        employer_id: employerId,
        business_email: formData.business_email || undefined,
        last_name: formData.last_name || undefined,
        first_name: formData.first_name || undefined,
        business_mobile: formData.business_mobile || undefined,
      });
      const found = res.data.status === 'success' ? (res.data.candidates || []) : [];
      if (found.length > 0) {
        setCandidates(found);
        setStep('duplicate');
      } else {
        await doSave();
      }
    } catch (err: any) {
      console.error('Error checking duplicates:', err);
      // 重複チェックに失敗した場合も登録は止めず、確認のうえ進められるようにする
      setError('重複チェックに失敗しました。そのまま登録する場合は「新規として登録」を押してください');
      setStep('duplicate');
    } finally {
      setChecking(false);
    }
  };

  const fullName = (c: any) =>
    `${c.last_name || ''} ${c.first_name || ''}`.trim() || '(氏名未設定)';
  const fullKana = (c: any) =>
    `${c.last_name_kana || ''} ${c.first_name_kana || ''}`.trim();

  const inputSx = {
    '& .MuiInputLabel-root': { fontSize: '13px' },
    '& .MuiOutlinedInput-input': { fontSize: '13px' },
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" disableScrollLock>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontSize: '0.875rem',
          fontWeight: 700,
          py: 0.75,
        }}
      >
        <Icons.PersonAdd sx={{ fontSize: 18 }} />
        {isEdit ? '先方担当者の編集' : '先方担当者の追加'}
        {employerName && (
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            （{employerName}）
          </Typography>
        )}
        <IconButton onClick={onClose} size="small" sx={{ ml: 'auto' }}>
          <Icons.Close sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {(loading || checking || saving) ? (
          <LoadingView
            message={checking ? '重複を確認中...' : saving ? '登録中...' : '読み込み中...'}
            height="200px"
            size={40}
          />
        ) : step === 'duplicate' ? (
          // ============================
          // 重複候補の確認ステップ
          // ============================
          <Box sx={{ mt: 1 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              同じ勤務先に、似た担当者が {candidates.length} 件見つかりました。
              重複登録を避けるため、既存の担当者をご確認ください。
            </Alert>
            <List dense sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              {candidates.map((c, idx) => (
                <React.Fragment key={c.id || idx}>
                  {idx > 0 && <Divider component="li" />}
                  <ListItem>
                    <ListItemText
                      primary={fullName(c)}
                      secondary={
                        [fullKana(c), c.department, c.occupation, c.business_email, c.business_mobile]
                          .filter(Boolean)
                          .join(' / ') || undefined
                      }
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              上記はいずれもこの取引先の先方担当者として既に登録済みです。
              別人であれば「新規として登録」してください。
            </Typography>
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
            )}
          </Box>
        ) : (
          // ============================
          // 入力ステップ
          // ============================
          <Box sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  label="姓"
                  required
                  fullWidth
                  size="small"
                  value={formData.last_name || ''}
                  onChange={(e) => handleChange('last_name', e.target.value)}
                  sx={inputSx}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="名"
                  fullWidth
                  size="small"
                  value={formData.first_name || ''}
                  onChange={(e) => handleChange('first_name', e.target.value)}
                  sx={inputSx}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="セイ"
                  fullWidth
                  size="small"
                  value={formData.last_name_kana || ''}
                  onChange={(e) => handleChange('last_name_kana', e.target.value)}
                  sx={inputSx}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="メイ"
                  fullWidth
                  size="small"
                  value={formData.first_name_kana || ''}
                  onChange={(e) => handleChange('first_name_kana', e.target.value)}
                  sx={inputSx}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="部署"
                  fullWidth
                  size="small"
                  value={formData.department || ''}
                  onChange={(e) => handleChange('department', e.target.value)}
                  placeholder="例: 営業部"
                  sx={inputSx}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="役職"
                  fullWidth
                  size="small"
                  value={formData.occupation || ''}
                  onChange={(e) => handleChange('occupation', e.target.value)}
                  placeholder="例: 課長"
                  sx={inputSx}
                />
              </Grid>
              <Grid size={12}>
                <TextField
                  label="メールアドレス"
                  fullWidth
                  size="small"
                  value={formData.business_email || ''}
                  onChange={(e) => handleChange('business_email', e.target.value)}
                  sx={inputSx}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="電話番号"
                  fullWidth
                  size="small"
                  value={formData.business_tel || ''}
                  onChange={(e) => handleChange('business_tel', e.target.value)}
                  placeholder="例: 03-1234-5678"
                  sx={inputSx}
                />
              </Grid>
              <Grid size={6}>
                <TextField
                  label="携帯番号"
                  fullWidth
                  size="small"
                  value={formData.business_mobile || ''}
                  onChange={(e) => handleChange('business_mobile', e.target.value)}
                  placeholder="例: 090-1234-5678"
                  sx={inputSx}
                />
              </Grid>
            </Grid>
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {step === 'duplicate' ? (
          <>
            <Button onClick={() => setStep('input')} sx={STANDARD_TEXT_BUTTON_SX}>
              入力に戻る
            </Button>
            <Button onClick={onClose} sx={STANDARD_TEXT_BUTTON_SX}>
              この担当者を使う
            </Button>
            <Button
              onClick={doSave}
              disabled={saving}
              startIcon={<Icons.PersonAdd />}
              sx={STANDARD_CTA_BUTTON_SX}
            >
              {saving ? '登録中...' : '新規として登録'}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={saving} sx={STANDARD_TEXT_BUTTON_SX}>
              キャンセル
            </Button>
            <Button
              onClick={handleNext}
              disabled={saving || checking || loading || !formData.last_name}
              startIcon={<Icons.Save />}
              sx={STANDARD_CTA_BUTTON_SX}
            >
              {checking ? '確認中...' : saving ? '保存中...' : isEdit ? '保存する' : '重複確認して登録'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ContactFormDialog;
